import { useEffect, useRef, useState } from 'react';
import { CandlestickSeries, createChart, HistogramSeries, LineSeries, LineStyle, type AutoscaleInfo, type IChartApi, type IPriceLine, type IPrimitivePaneRenderer, type IPrimitivePaneView, type ISeriesApi, type Logical, type ISeriesPrimitive, type SeriesAttachedParameter, type Time, type UTCTimestamp } from 'lightweight-charts';
import type { ChartPattern, ChartResponse, ContractPrices, DeskSnapshot, FormingPattern } from '../api/generated/types.gen';
import { get } from '../api/desk';

/** A strike, or a range bucket's floor with its upper edge (the next bucket's floor). */
export type RailStrike = { ticker: string; strike: number; live: boolean; upper?: number | null };
/** Pixel positions on the candle pane, measured by the chart itself. */
export type RailGeometry = { height: number; ys: ReadonlyMap<string, number>; uppers: ReadonlyMap<string, number>; spot: number | null };
export type PriceSpan = { low: number; high: number };
/** A held position's level on the coin chart: a strike or target line, or a range bucket's two edges. */
export type TradeLevel = { key: string; side: 'yes' | 'no'; low: number; high: number | null; label: string; selected: boolean };
/** Plan 063: a held position's coin stop, sold when a 15-second candle closes beyond it (below for up, above for down). */
export type CoinStop = { key: string; price: number; direction: 'up' | 'down' };
/** The selected contract while it is held: its own price pane with the entry and exits (plan 014). */
// Plan 066: a held contract's entry and take-profit on its price pane; its stop is the coin stop, drawn on the coin chart.
export type HeldContract = { ticker: string; side: 'yes' | 'no'; name: string; entry: number; takeProfit: number | null };

const INTERVAL_SECONDS: Record<string, number> = { '10s': 10, '15s': 15, '30s': 30, '1m': 60, '5m': 300, '15m': 900, '1h': 3600, '4h': 14400 };
const token = (name: string) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();
const coinFormat = (decimals: number) => (p: number) => p.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
const centsFormat = (p: number) => `${(Math.round(p * 1000) / 10).toLocaleString('en-US', { maximumFractionDigits: 1 })}¢`;
const bestBid = (levels: string[][] | undefined) => { const bids = (levels ?? []).filter(l => Number(l[1]) > 0).map(l => Number(l[0])); return bids.length ? Math.max(...bids) : null; };
const alpha = (hex: string, a: number) => { const n = parseInt(hex.slice(1), 16); return `rgba(${n >> 16},${(n >> 8) & 255},${n & 255},${a})`; };

/**
 * Draws nothing. On every chart redraw (scroll, zoom, price-scale drag, resize, new data) it maps each strike
 * through the candle series' own price scale and publishes the result once per animation frame. It also widens
 * autoscale to the strikes that matter: the selected one and those still trading between 2¢ and 98¢.
 */
class StrikeRailPrimitive implements ISeriesPrimitive<Time> {
  private attachedTo: SeriesAttachedParameter<Time> | null = null;
  private strikes: RailStrike[] = [];
  private selected: number[] = [];
  private spot: number | null = null;
  private frame = 0;
  private published = '';
  private pending: RailGeometry | null = null;
  constructor(private readonly publish: (geometry: RailGeometry) => void) {}
  attached(param: SeriesAttachedParameter<Time>) { this.attachedTo = param; }
  detached() { this.attachedTo = null; cancelAnimationFrame(this.frame); }
  update(strikes: RailStrike[], selected: number[], spot: number | null) {
    this.strikes = strikes; this.selected = selected; this.spot = spot;
    this.attachedTo?.requestUpdate();
  }
  updateAllViews() {
    const p = this.attachedTo;
    if (!p) return;
    const ys = new Map<string, number>(), uppers = new Map<string, number>();
    for (const s of this.strikes) {
      const y = p.series.priceToCoordinate(s.strike); if (y != null) ys.set(s.ticker, y);
      const top = s.upper == null ? null : p.series.priceToCoordinate(s.upper); if (top != null) uppers.set(s.ticker, top);
    }
    const spot = this.spot == null ? null : p.series.priceToCoordinate(this.spot);
    const geometry = { height: p.chart.paneSize(0).height, ys, uppers, spot };
    const key = `${geometry.height}|${spot}|${[...ys.values()].join(',')}|${[...uppers.values()].join(',')}`;
    if (key === this.published) return;
    this.published = key; this.pending = geometry;
    if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; if (this.pending) this.publish(this.pending); });
  }
  autoscaleInfo(): AutoscaleInfo | null {
    const focus = this.strikes.filter(s => s.live).flatMap(s => s.upper == null ? [s.strike] : [s.strike, s.upper]);
    focus.push(...this.selected);
    if (!focus.length) return null;
    // Pad by one and a half strike steps so edge strikes clear the rail's header and "more" markers.
    const sorted = this.strikes.map(s => s.strike).sort((a, b) => a - b);
    let step = Infinity;
    for (let i = 1; i < sorted.length; i++) if (sorted[i] > sorted[i - 1]) step = Math.min(step, sorted[i] - sorted[i - 1]);
    const pad = Number.isFinite(step) ? step * 1.5 : 0;
    return { priceRange: { minValue: Math.min(...focus) - pad, maxValue: Math.max(...focus) + pad } };
  }
}

/** Shades the selected range bucket from its floor to its upper edge, across the pane (plan 007, phase 3). */
class BucketBand implements ISeriesPrimitive<Time>, IPrimitivePaneView {
  private attachedTo: SeriesAttachedParameter<Time> | null = null;
  private span: PriceSpan | null = null;
  constructor(private readonly color: string) {}
  attached(param: SeriesAttachedParameter<Time>) { this.attachedTo = param; }
  detached() { this.attachedTo = null; }
  set(span: PriceSpan | null) { this.span = span; this.attachedTo?.requestUpdate(); }
  paneViews() { return [this]; }
  zOrder() { return 'bottom' as const; }
  renderer(): IPrimitivePaneRenderer | null {
    const p = this.attachedTo, span = this.span;
    if (!p || !span) return null;
    const top = p.series.priceToCoordinate(span.high), bottom = p.series.priceToCoordinate(span.low);
    if (top == null || bottom == null) return null;
    const color = this.color;
    return { draw: (target: Parameters<IPrimitivePaneRenderer['draw']>[0]) => target.useBitmapCoordinateSpace(({ context, verticalPixelRatio, bitmapSize }) => {
      context.fillStyle = color;
      context.fillRect(0, Math.round(top * verticalPixelRatio), bitmapSize.width, Math.max(1, Math.round((bottom - top) * verticalPixelRatio)));
    }) };
  }
}

/**
 * A fractional bar position in pixels. lightweight-charts 5.2 returns 0 for a fractional logical index, so
 * interpolate between the two whole bars around it (also past the last bar, where the time scale is still spaced).
 */
function between(scale: ReturnType<IChartApi['timeScale']>, logical: number): number | null {
  const i = Math.floor(logical), a = scale.logicalToCoordinate(i as Logical), b = scale.logicalToCoordinate((i + 1) as Logical);
  return a == null || b == null ? null : a + (logical - i) * (b - a);
}

/** Earlier periods narrower than this on screen are not marked: hourly on 4h candles would be a wall of lines. */
const MIN_PERIOD_PX = 12;

/**
 * Shades the open 15-minute window between its start and close, behind the candles (plan 007).
 *
 * Both edges are placed by bar position, not only by exact bar times: the close is in the future (no bar yet) and
 * the start may fall inside a bar on 1h or 4h candles. A time on a bar uses that bar's coordinate; any other time is
 * placed by its distance in bars from the last bar, so the band keeps its place through zoom, scroll and refreshes. *
 * Earlier periods of the same length are marked by faint dashed lines at their starts (plan 031), placed the same
 * way, so the history reads in settlement periods; they are skipped when a period is narrower than MIN_PERIOD_PX.
 */
type TimeAxis = { last: number; seconds: number; count: number };
/** A time's x position: a bar's own, or placed between or after bars from the loaded bar spacing. */
function placeTime(p: SeriesAttachedParameter<Time> | null, axis: TimeAxis | null, t: number): number | null {
  if (!p) return null;
  const scale = p.chart.timeScale();
  const exact = scale.timeToCoordinate(t as UTCTimestamp);
  if (exact != null) return exact;
  if (!axis || axis.count === 0) return null;
  return between(scale, axis.count - 1 + (t - axis.last) / axis.seconds);
}

/**
 * Plan 061: for each gold candle, a dotted line from the swing top or bottom it broke to the candle, with a dot on
 * the swing and a small arrow at the candle. Green turning up, red turning down.
 */
/** `tip` is the gold candle's low (turning up) or high (turning down), where its arrow sits, as in the mockup. */
type Break = { time: number; direction: 'up' | 'down'; from: number; price: number; tip: number };
class BreakLines implements ISeriesPrimitive<Time>, IPrimitivePaneView {
  private attachedTo: SeriesAttachedParameter<Time> | null = null;
  private breaks: Break[] = [];
  private axis: TimeAxis | null = null;
  constructor(private readonly colors: { up: string; down: string; gold: string }) {}
  attached(param: SeriesAttachedParameter<Time>) { this.attachedTo = param; }
  detached() { this.attachedTo = null; }
  set(breaks: Break[], axis: TimeAxis | null) { this.breaks = breaks; this.axis = axis; this.attachedTo?.requestUpdate(); }
  paneViews() { return [this]; }
  zOrder() { return 'top' as const; }
  /** Each break's segment in pixels, for drawing and the test hook. */
  segments(): { left: number; right: number; y: number; tip: number; direction: 'up' | 'down' }[] {
    const p = this.attachedTo;
    if (!p) return [];
    return this.breaks.flatMap(b => {
      const left = placeTime(p, this.axis, b.from), right = placeTime(p, this.axis, b.time);
      const y = p.series.priceToCoordinate(b.price), tip = p.series.priceToCoordinate(b.tip);
      return left == null || right == null || y == null || tip == null ? [] : [{ left, right, y, tip, direction: b.direction }];
    });
  }
  renderer(): IPrimitivePaneRenderer | null {
    const segments = this.segments(), colors = this.colors;
    if (!segments.length) return null;
    return { draw: (target: Parameters<IPrimitivePaneRenderer['draw']>[0]) => target.useBitmapCoordinateSpace(({ context, horizontalPixelRatio: hr, verticalPixelRatio: vr }) => {
      for (const s of segments) {
        const y = Math.round(s.y * vr), left = Math.round(s.left * hr), right = Math.round(s.right * hr);
        context.fillStyle = s.direction === 'up' ? colors.up : colors.down;
        // Dashes are filled: a stroked path doesn't render in this pane's bitmap space.
        for (let x = left; x < right; x += Math.round(6 * hr)) context.fillRect(x, y, Math.max(1, Math.round(3 * hr)), Math.max(1, Math.round(vr)));
        context.beginPath(); context.arc(left, y, 2.6 * hr, 0, 2 * Math.PI); context.fill();
        context.fillStyle = colors.gold;
        context.font = `${Math.round(10 * vr)}px sans-serif`; context.textAlign = 'center';
        const tip = Math.round(s.tip * vr);
        context.fillText(s.direction === 'up' ? '▲' : '▼', right, s.direction === 'up' ? tip + Math.round(14 * vr) : tip - Math.round(6 * vr));
      }
    }) };
  }
}

/**
 * Plan 064: finished chart patterns from the server's 5-candle swings, as history. The swings are joined (a wedge or
 * channel draws its two lines instead), the neckline is dashed, the name sits above a top or below a bottom, and a
 * circle with an arrow marks the candle that closed through it, green up and red down. Plan 067: each is drawn in its
 * family's colour, fainter than the patterns forming now; failed and interrupted ones fainter still.
 */
type Spot = { x: number; y: number };
type Family = FormingPattern['family'];
/** Plan 067: one colour per family; green and red stay for breaks, orange for strikes, gold for reversals, blue for the index. */
const FAMILY_TOKEN: Record<Family, string> = { triangle: '--pat-triangle', wedge: '--pat-wedge', channel: '--pat-channel', reversal: '--pat-reversal', broadening: '--pat-broadening' };
const FINISHED_FAMILY: Record<ChartPattern['kind'], Family> = {
  head_and_shoulders: 'reversal', inverse_head_and_shoulders: 'reversal', double_top: 'reversal', double_bottom: 'reversal',
  triangle_top: 'triangle', triangle_bottom: 'triangle', rectangle_top: 'channel', rectangle_bottom: 'channel',
  broadening_top: 'broadening', broadening_bottom: 'broadening', rising_wedge: 'wedge', falling_wedge: 'wedge', channel_up: 'channel', channel_down: 'channel',
};
type Shape = { name: string; status: ChartPattern['status']; family: Family; above: boolean; joined: Spot[]; lines: Spot[][]; dashed: boolean;
  label: Spot; completed: (Spot & { direction: 'up' | 'down' }) | null };
/** Patterns whose name goes above them: tops, and shapes that turn or run down. */
const ABOVE = new Set(['head_and_shoulders', 'double_top', 'triangle_top', 'rectangle_top', 'broadening_top', 'rising_wedge', 'channel_down']);
const LINE_KINDS = new Set(['rising_wedge', 'falling_wedge', 'channel_up', 'channel_down']);
class PatternLines implements ISeriesPrimitive<Time>, IPrimitivePaneView {
  private attachedTo: SeriesAttachedParameter<Time> | null = null;
  private patterns: ChartPattern[] = [];
  private axis: TimeAxis | null = null;
  constructor(private readonly colors: { family: Record<Family, string>; text: string; halo: string; up: string; down: string }) {}
  attached(param: SeriesAttachedParameter<Time>) { this.attachedTo = param; }
  detached() { this.attachedTo = null; }
  set(patterns: ChartPattern[], axis: TimeAxis | null) { this.patterns = patterns; this.axis = axis; this.attachedTo?.requestUpdate(); }
  paneViews() { return [this]; }
  zOrder() { return 'top' as const; }
  /** Each pattern in pixels, for drawing and the test hook; one with a point off the price scale is skipped. */
  shapes(): Shape[] {
    const p = this.attachedTo;
    if (!p) return [];
    const at = (time: number, price: string | number): Spot | null => {
      const x = placeTime(p, this.axis, time), y = p.series.priceToCoordinate(Number(price));
      return x == null || y == null ? null : { x, y };
    };
    const labels: Spot[] = [];
    return this.patterns.flatMap(q => {
      const joined = q.points.map(pt => at(pt.time, pt.price));
      const lines = q.lines.map(l => l.map(pt => at(pt.time, pt.price)));
      if (joined.some(s => !s) || lines.some(l => l.some(s => !s))) return [];
      const spots = joined as Spot[], above = ABOVE.has(q.kind);
      const edge = above ? Math.min(...spots.map(s => s.y)) : Math.max(...spots.map(s => s.y));
      const x = (Math.min(...spots.map(s => s.x)) + Math.max(...spots.map(s => s.x))) / 2;
      const label = { x, y: above ? edge - 8 : edge + 16 };
      while (labels.some(l => Math.abs(l.x - label.x) < 110 && Math.abs(l.y - label.y) < 13)) label.y += above ? -13 : 13;
      labels.push(label);
      const done = q.completed ? at(q.completed.time, q.completed.price) : null;
      return [{ name: q.name, status: q.status, family: FINISHED_FAMILY[q.kind], above, joined: LINE_KINDS.has(q.kind) ? [] : spots, lines: lines as Spot[][],
        dashed: !LINE_KINDS.has(q.kind), label, completed: done && q.completed ? { ...done, direction: q.completed.direction } : null }];
    });
  }
  renderer(): IPrimitivePaneRenderer | null {
    const shapes = this.shapes(), colors = this.colors;
    if (!shapes.length) return null;
    return { draw: (target: Parameters<IPrimitivePaneRenderer['draw']>[0]) => target.useBitmapCoordinateSpace(({ context: g, horizontalPixelRatio: hr, verticalPixelRatio: vr, bitmapSize }) => {
      const X = (s: Spot) => s.x * hr, Y = (s: Spot) => s.y * vr;
      // A name near the top or bottom edge stays on the pane.
      const labelY = (s: Spot) => Math.min(Math.max(Y(s), 14 * vr), bitmapSize.height - 4 * vr);
      for (const s of shapes) {
        g.globalAlpha = s.status === 'failed' || s.status === 'interrupted' ? 0.3 : 0.55;
        const line = colors.family[s.family];
        g.strokeStyle = line; g.fillStyle = line; g.lineJoin = 'round';
        g.lineWidth = 1.6 * hr; g.setLineDash([]);
        if (s.joined.length) {
          g.beginPath(); s.joined.forEach((pt, i) => (i ? g.lineTo(X(pt), Y(pt)) : g.moveTo(X(pt), Y(pt)))); g.stroke();
          for (const pt of s.joined) { g.beginPath(); g.arc(X(pt), Y(pt), 2.4 * hr, 0, 2 * Math.PI); g.fill(); }
        }
        g.lineWidth = (s.dashed ? 1 : 1.6) * hr; g.setLineDash(s.dashed ? [4 * hr, 3 * hr] : []);
        for (const [a, b] of s.lines) { g.beginPath(); g.moveTo(X(a), Y(a)); g.lineTo(X(b), Y(b)); g.stroke(); }
        g.setLineDash([]);
        g.font = `${Math.round(11 * vr)}px "IBM Plex Sans", sans-serif`; g.textAlign = 'center';
        g.lineWidth = 3 * hr; g.strokeStyle = colors.halo; g.strokeText(s.name, X(s.label), labelY(s.label));
        g.fillStyle = colors.text; g.fillText(s.name, X(s.label), labelY(s.label));
        if (s.completed) {
          const c = s.completed, color = c.direction === 'up' ? colors.up : colors.down;
          g.strokeStyle = color; g.fillStyle = color; g.lineWidth = 1.6 * hr;
          g.beginPath(); g.arc(X(c), Y(c), 5 * hr, 0, 2 * Math.PI); g.stroke();
          g.font = `${Math.round(11 * vr)}px "IBM Plex Mono", monospace`;
          g.fillText(c.direction === 'up' ? '▲' : '▼', X(c), Y(c) + (c.direction === 'up' ? -8 : 16) * vr);
        }
      }
      g.globalAlpha = 1;
    }) };
  }
}

/**
 * Plan 067: chart patterns forming now, from the server. Each is drawn in its family's colour and shaded between its
 * lines, solid up to the newest candle and dashed ahead of it, with its name and "forming". A double or head and
 * shoulders joins its swings and runs its neckline ahead. One that broke keeps its shape up to the break, marked with a
 * circle and an arrow. The break prices are axis labels, set by the chart (see PriceChart).
 */
type FormingShape = { name: string; status: FormingPattern['status']; family: Family; joined: Spot[]; lines: { start: Spot; now: Spot; ahead: Spot }[];
  label: Spot; broke: (Spot & { direction: 'up' | 'down' }) | null };
class FormingLines implements ISeriesPrimitive<Time>, IPrimitivePaneView {
  private attachedTo: SeriesAttachedParameter<Time> | null = null;
  private patterns: FormingPattern[] = [];
  private axis: TimeAxis | null = null;
  constructor(private readonly colors: { family: Record<Family, string>; halo: string; up: string; down: string }) {}
  attached(param: SeriesAttachedParameter<Time>) { this.attachedTo = param; }
  detached() { this.attachedTo = null; }
  set(patterns: FormingPattern[], axis: TimeAxis | null) { this.patterns = patterns; this.axis = axis; this.attachedTo?.requestUpdate(); }
  paneViews() { return [this]; }
  zOrder() { return 'top' as const; }
  /** Each pattern in pixels, for drawing and the test hook; one with a point off the price scale is skipped. */
  shapes(): FormingShape[] {
    const p = this.attachedTo;
    if (!p) return [];
    const at = (time: number, price: string): Spot | null => {
      const x = placeTime(p, this.axis, time), y = p.series.priceToCoordinate(Number(price));
      return x == null || y == null ? null : { x, y };
    };
    return this.patterns.flatMap(q => {
      const joined = q.points.map(pt => at(pt.time, pt.price));
      const lines = q.lines.map(l => ({ start: at(l.start.time, l.start.price), now: at(l.now.time, l.now.price), ahead: at(l.ahead.time, l.ahead.price) }));
      if (joined.some(s => !s) || lines.some(l => !l.start || !l.now || !l.ahead)) return [];
      const spots = joined as Spot[], top = Math.min(...spots.map(s => s.y), ...lines.map(l => Math.min(l.start!.y, l.now!.y)));
      const done = q.broke ? at(q.broke.time, q.broke.price) : null;
      return [{ name: q.name, status: q.status, family: q.family, joined: q.lines.length === 2 ? [] : spots,
        lines: lines as { start: Spot; now: Spot; ahead: Spot }[], label: { x: spots[0].x, y: top - 8 },
        broke: done && q.broke ? { ...done, direction: q.broke.direction } : null }];
    });
  }
  renderer(): IPrimitivePaneRenderer | null {
    const shapes = this.shapes(), colors = this.colors;
    if (!shapes.length) return null;
    return { draw: (target: Parameters<IPrimitivePaneRenderer['draw']>[0]) => target.useBitmapCoordinateSpace(({ context: g, horizontalPixelRatio: hr, verticalPixelRatio: vr, bitmapSize }) => {
      const X = (s: Spot) => s.x * hr, Y = (s: Spot) => s.y * vr;
      const shade = (a: Spot, b: Spot, c: Spot, d: Spot, alpha: number, color: string) => {
        g.globalAlpha = alpha; g.fillStyle = color; g.beginPath(); g.moveTo(X(a), Y(a)); g.lineTo(X(b), Y(b)); g.lineTo(X(c), Y(c)); g.lineTo(X(d), Y(d)); g.closePath(); g.fill();
      };
      for (const s of shapes) {
        const color = colors.family[s.family], [upper, lower] = s.lines;
        // The shape between its two lines: shaded to the newest candle, fainter ahead of it (or to the break).
        if (upper && lower) {
          // Broadening's near-white shades as strongly as the others' colours at about half the opacity.
          const fill = s.family === 'broadening' ? 0.06 : 0.13;
          shade(upper.start, upper.now, lower.now, lower.start, fill, color);
          if (!s.broke) shade(upper.now, upper.ahead, lower.ahead, lower.now, fill / 2, color);
        }
        g.globalAlpha = 1; g.strokeStyle = color; g.fillStyle = color; g.lineJoin = 'round';
        if (s.joined.length) {
          g.lineWidth = 1.6 * hr; g.setLineDash([]);
          g.beginPath(); s.joined.forEach((pt, i) => (i ? g.lineTo(X(pt), Y(pt)) : g.moveTo(X(pt), Y(pt)))); g.stroke();
        }
        for (const l of s.lines) {
          g.lineWidth = 2 * hr; g.setLineDash([]);
          g.beginPath(); g.moveTo(X(l.start), Y(l.start)); g.lineTo(X(l.now), Y(l.now)); g.stroke();
          if (!s.broke) {
            g.lineWidth = 1.6 * hr; g.setLineDash([5 * hr, 4 * hr]);
            g.beginPath(); g.moveTo(X(l.now), Y(l.now)); g.lineTo(X(l.ahead), Y(l.ahead)); g.stroke();
          }
        }
        g.setLineDash([]);
        for (const pt of s.joined.length ? s.joined : []) { g.beginPath(); g.arc(X(pt), Y(pt), 2.8 * hr, 0, 2 * Math.PI); g.fill(); }
        const text = s.broke ? `${s.name} broke ${s.broke.direction}` : `${s.name} forming`;
        const ly = Math.min(Math.max(Y(s.label), 14 * vr), bitmapSize.height - 4 * vr);
        g.font = `600 ${Math.round(11.5 * vr)}px "IBM Plex Sans", sans-serif`; g.textAlign = 'left';
        g.lineWidth = 3 * hr; g.strokeStyle = colors.halo; g.strokeText(text, X(s.label), ly);
        g.fillStyle = color; g.fillText(text, X(s.label), ly);
        if (s.broke) {
          const c = s.broke, mark = c.direction === 'up' ? colors.up : colors.down;
          g.strokeStyle = mark; g.fillStyle = mark; g.lineWidth = 2 * hr;
          g.beginPath(); g.arc(X(c), Y(c), 6 * hr, 0, 2 * Math.PI); g.stroke();
          g.font = `${Math.round(12 * vr)}px "IBM Plex Mono", monospace`; g.textAlign = 'center';
          g.fillText(c.direction === 'up' ? '▲' : '▼', X(c), Y(c) + (c.direction === 'up' ? -10 : 20) * vr);
        }
      }
      g.globalAlpha = 1;
    }) };
  }
}

/** Plan 057: the selected contract's settlement window, the 60 seconds before its close that Kalshi averages. */
class SettlementStrip implements ISeriesPrimitive<Time>, IPrimitivePaneView {
  private attachedTo: SeriesAttachedParameter<Time> | null = null;
  private close: number | null = null;
  private axis: TimeAxis | null = null;
  constructor(private readonly color: string) {}
  attached(param: SeriesAttachedParameter<Time>) { this.attachedTo = param; }
  detached() { this.attachedTo = null; }
  set(close: number | null) { this.close = close; this.attachedTo?.requestUpdate(); }
  setAxis(axis: TimeAxis | null) { this.axis = axis; this.attachedTo?.requestUpdate(); }
  paneViews() { return [this]; }
  zOrder() { return 'bottom' as const; }
  edges(): { left: number; right: number } | null {
    if (this.close == null) return null;
    const left = placeTime(this.attachedTo, this.axis, this.close - 60), right = placeTime(this.attachedTo, this.axis, this.close);
    if (left == null || right == null || right <= left) return null;
    return { left, right };
  }
  renderer(): IPrimitivePaneRenderer | null {
    const e = this.edges(), color = this.color;
    if (!e) return null;
    return { draw: (target: Parameters<IPrimitivePaneRenderer['draw']>[0]) => target.useBitmapCoordinateSpace(({ context, horizontalPixelRatio, bitmapSize }) => {
      context.fillStyle = color;
      // At least 2px, so a minute stays visible on long timeframes.
      context.fillRect(Math.round(e.left * horizontalPixelRatio), 0, Math.max(2, Math.round((e.right - e.left) * horizontalPixelRatio)), bitmapSize.height);
    }) };
  }
}

class WindowBand implements ISeriesPrimitive<Time>, IPrimitivePaneView {
  private attachedTo: SeriesAttachedParameter<Time> | null = null;
  private span: { start: number; end: number } | null = null;
  private axis: TimeAxis | null = null;
  private bars: { time: number; open: number; close: number }[] = [];
  private results = new Map<number, boolean>();
  constructor(private readonly color: string, private readonly divider: string, private readonly tint: { up: string; down: string }) {}
  attached(param: SeriesAttachedParameter<Time>) { this.attachedTo = param; }
  detached() { this.attachedTo = null; }
  set(span: { start: number; end: number } | null) { this.span = span; this.attachedTo?.requestUpdate(); }
  /** The loaded candles, for an earlier period's own open and close. */
  setBars(bars: { time: number; open: number; close: number }[]) { this.bars = bars; this.attachedTo?.requestUpdate(); }
  /** Settled results by period close (true when it settled up), which win over the candles (15-minute windows). */
  setResults(results: Map<number, boolean>) { this.results = results; this.attachedTo?.requestUpdate(); }
  /** The newest bar's time, the bar length and how many bars are loaded, for placing times between or after bars. */
  setAxis(axis: TimeAxis | null) { this.axis = axis; this.attachedTo?.requestUpdate(); }
  paneViews() { return [this]; }
  zOrder() { return 'bottom' as const; }
  private x(t: number): number | null { return placeTime(this.attachedTo, this.axis, t); }
  /** Left and right pixel edges, or null when the band can't be placed (no bars yet). */
  edges(): { left: number; right: number } | null {
    const span = this.span;
    if (!span) return null;
    const left = this.x(span.start), right = this.x(span.end);
    return left == null || right == null || right <= left ? null : { left, right };
  }
  /** X positions of the starts of earlier periods, newest first, while they are on screen and readable. */
  dividers(): number[] {
    const span = this.span;
    if (!span || span.end <= span.start) return [];
    const period = span.end - span.start, start = this.x(span.start), xs: number[] = [];
    if (start == null) return [];
    for (let k = 1; k <= 2000; k++) {
      const x = this.x(span.start - k * period);
      if (x == null || x < 0) break;
      xs.push(x);
    }
    return xs.length && start - xs[0] < MIN_PERIOD_PX ? [] : xs;
  }
  /**
   * Earlier periods on screen with their direction (plan 033): a settled result when one is known, otherwise the
   * period's first candle open against its last close. Periods without candles are left untinted.
   */
  periods(): { left: number; right: number; up: boolean }[] {
    const span = this.span, start = span ? this.x(span.start) : null, lines = this.dividers();
    if (!span || start == null || !lines.length) return [];
    const period = span.end - span.start, out: { left: number; right: number; up: boolean }[] = [];
    for (let k = 1; k <= lines.length + 1; k++) {
      const from = span.start - k * period, to = from + period;
      const right = k === 1 ? start : lines[k - 2], x = this.x(from);
      const left = k <= lines.length ? lines[k - 1] : Math.max(0, x ?? 0);
      if (right <= 0) break;
      let up = this.results.get(to);
      if (up == null) {
        const inside = this.bars.filter(b => b.time >= from && b.time < to);
        if (inside.length) up = inside[inside.length - 1].close >= inside[0].open;
      }
      if (up != null) out.push({ left, right, up });
    }
    return out;
  }
  renderer(): IPrimitivePaneRenderer | null {
    const e = this.edges(), lines = this.dividers(), periods = this.periods();
    if (!e && !lines.length) return null;
    const color = this.color, divider = this.divider, tint = this.tint;
    return { draw: (target: Parameters<IPrimitivePaneRenderer['draw']>[0]) => target.useBitmapCoordinateSpace(({ context, horizontalPixelRatio, verticalPixelRatio, bitmapSize }) => {
      for (const p of periods) {
        context.fillStyle = p.up ? tint.up : tint.down;
        context.fillRect(Math.round(p.left * horizontalPixelRatio), 0, Math.max(1, Math.round((p.right - p.left) * horizontalPixelRatio)), bitmapSize.height);
      }
      if (e) {
        context.fillStyle = color;
        context.fillRect(Math.round(e.left * horizontalPixelRatio), 0, Math.max(1, Math.round((e.right - e.left) * horizontalPixelRatio)), bitmapSize.height);
      }
      // Dashes are filled, like the band: a stroked path doesn't render in this pane's bitmap space.
      context.fillStyle = divider;
      const width = Math.max(1, Math.round(horizontalPixelRatio)), dash = 3 * verticalPixelRatio, step = 7 * verticalPixelRatio;
      for (const x of lines) {
        const px = Math.round(x * horizontalPixelRatio);
        for (let y = 0; y < bitmapSize.height; y += step) context.fillRect(px, Math.round(y), width, Math.round(dash));
      }
    }) };
  }
}

export function PriceChart({ desk, interval, reversalMarks = true, patternMarks = true, indexLine = true, strikes, selectedStrike, selectedBand = null, levels = [], coinStops = [], contract = null, window: span = null }: {
  desk: DeskSnapshot; interval: string; strikes: RailStrike[]; selectedStrike: number | null;
  /** Draw completed reversal candles in gold (plan 041). */
  reversalMarks?: boolean;
  /** Draw chart patterns (plan 064). */
  patternMarks?: boolean;
  /** Draw the CF Benchmarks settlement index the contracts settle on (plan 057). */
  indexLine?: boolean;
  /** Open positions on the contracts shown, with their entry and P&L labels. */
  levels?: TradeLevel[];
  /** Held positions' coin stops, drawn in red at the swing price. */
  coinStops?: CoinStop[];
  contract?: HeldContract | null;
  window?: { start: number; end: number } | null;
  /** The selected range bucket: shaded between two edge lines in place of the strike line. */
  selectedBand?: PriceSpan | null;
}) {
  const host = useRef<HTMLDivElement>(null);
  const chart = useRef<IChartApi | null>(null);
  const candle = useRef<ISeriesApi<'Candlestick'> | null>(null);
  // Plan 047: the newest candle as last drawn, moved by each Coinbase price on the stream between reloads.
  const live = useRef<{ time: number; open: number; high: number; low: number; close: number } | null>(null);
  const volume = useRef<ISeriesApi<'Histogram'> | null>(null);
  const indexSeries = useRef<ISeriesApi<'Line'> | null>(null);
  const strip = useRef<SettlementStrip | null>(null);
  const breakLines = useRef<BreakLines | null>(null);
  const patternLines = useRef<PatternLines | null>(null);
  const formingLines = useRef<FormingLines | null>(null);
  const rail = useRef<StrikeRailPrimitive | null>(null);
  const band = useRef<WindowBand | null>(null);
  const bucket = useRef<BucketBand | null>(null);
  const bid = useRef<ISeriesApi<'Line'> | null>(null);
  const ask = useRef<ISeriesApi<'Line'> | null>(null);
  const [prices, setPrices] = useState<ContractPrices | null>(null);
  const exitLevels = useRef<number[]>([]);
  // Where the contract pane starts: the candle pane's height, measured by the chart.
  const [paneTop, setPaneTop] = useState(0);
  const decimals = desk.decimals ?? 2, coin = desk.coin ?? 'BTC';
  const [bars, setBars] = useState<ChartResponse | null>(null);
  const [error, setError] = useState('');
  const latest = useRef(desk); latest.current = desk;
  const fitted = useRef('');
  const colors = useRef({ up: '', down: '', muted: '', gold: '' });
  const marked = useRef<number[]>([]);
  useEffect(() => {
    if (!host.current) return;
    const t = { panel: token('--panel'), grid: token('--grid'), line: token('--line2'), muted: token('--muted'), faint: token('--faint'), up: token('--up'), down: token('--down'), amber: token('--amber'), accent: token('--accent'), text: token('--text') };
    colors.current = { up: t.up, down: t.down, muted: t.faint, gold: token('--gold') };
    const c = createChart(host.current, {
      autoSize: true, layout: { background: { color: t.panel }, textColor: t.muted, fontFamily: '"IBM Plex Mono", "Courier New", monospace', fontSize: 11, attributionLogo: true },
      // No vertical grid: it is painted over the period dividers (plan 031), which give the time structure instead.
      grid: { vertLines: { visible: false }, horzLines: { color: t.grid } },
      rightPriceScale: { borderColor: t.line, scaleMargins: { top: .08, bottom: .14 } },
      timeScale: { borderColor: t.line, timeVisible: true, secondsVisible: false, rightOffset: 5 },
      crosshair: { vertLine: { color: t.faint, labelBackgroundColor: t.line }, horzLine: { color: t.faint, labelBackgroundColor: t.line } },
      // Prices are formatted per series (coin precision on the candles, cents on the contract pane).
      localization: { locale: 'en-US', timeFormatter: (time: number) => new Date(time * 1000).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) },
    });
    chart.current = c;
    candle.current = c.addSeries(CandlestickSeries, { upColor: t.up, downColor: t.down, borderVisible: false, wickUpColor: t.up, wickDownColor: t.down, priceLineVisible: true, priceLineColor: t.muted });
    volume.current = c.addSeries(HistogramSeries, { priceFormat: { type: 'volume' }, priceScaleId: 'volume', lastValueVisible: false, priceLineVisible: false });
    volume.current.priceScale().applyOptions({ scaleMargins: { top: .88, bottom: 0 } });
    // Plan 065: the price these contracts settle on, in the desk's blue: a solid 2-pixel line, clear over the candles
    // without crowding them.
    indexSeries.current = c.addSeries(LineSeries, { color: t.accent, lineWidth: 2, lineStyle: LineStyle.Solid, lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false });
    rail.current = new StrikeRailPrimitive(g => setPaneTop(g.height));
    candle.current.attachPrimitive(rail.current);
    band.current = new WindowBand(alpha(t.amber, .08), alpha(t.faint, .7), { up: alpha(t.up, .07), down: alpha(t.down, .1) });
    candle.current.attachPrimitive(band.current);
    bucket.current = new BucketBand(alpha(t.amber, .12));
    candle.current.attachPrimitive(bucket.current);
    strip.current = new SettlementStrip(alpha(t.amber, .22));
    candle.current.attachPrimitive(strip.current);
    breakLines.current = new BreakLines({ up: alpha(t.up, .85), down: alpha(t.down, .85), gold: token('--gold') });
    candle.current.attachPrimitive(breakLines.current);
    const family = Object.fromEntries(Object.entries(FAMILY_TOKEN).map(([f, name]) => [f, token(name)])) as Record<Family, string>;
    patternLines.current = new PatternLines({ family, text: t.text, halo: t.panel, up: t.up, down: t.down });
    candle.current.attachPrimitive(patternLines.current);
    formingLines.current = new FormingLines({ family, halo: t.panel, up: t.up, down: t.down });
    candle.current.attachPrimitive(formingLines.current);
    // Read-only test hooks compare alignment and viewport behavior with the chart's own scales.
    if (import.meta.env.MODE === 'test') Object.assign(window, { __ctChart: { panes: () => c.panes().length, contractLine: () => bid.current?.data().map(d => ({ time: d.time, value: 'value' in d ? d.value : null })) ?? null, contractLines: () => bid.current?.priceLines().map(l => ({ title: l.options().title, price: l.options().price })) ?? [], priceLines: () => candle.current?.priceLines().map(l => ({ title: l.options().title, price: l.options().price })) ?? [], priceToCoordinate: (p: number) => candle.current?.priceToCoordinate(p) ?? null, visibleTimeRange: () => c.timeScale().getVisibleRange(), colors: () => ({ up: candle.current?.options().upColor, down: candle.current?.options().downColor }), windowBand: () => band.current?.edges() ?? null, settlementStrip: () => strip.current?.edges() ?? null, indexLine: () => indexSeries.current?.data().map(d => ({ time: d.time, value: 'value' in d ? d.value : null, hidden: 'color' in d && d.color === 'rgba(0,0,0,0)' })) ?? [], indexStyle: () => ({ width: indexSeries.current?.options().lineWidth, style: indexSeries.current?.options().lineStyle, label: indexSeries.current?.options().lastValueVisible, color: indexSeries.current?.options().color }), periodDividers: () => band.current?.dividers() ?? [], periodTints: () => band.current?.periods() ?? [], reversals: () => marked.current, breakLines: () => breakLines.current?.segments() ?? [], patterns: () => patternLines.current?.shapes() ?? [], forming: () => formingLines.current?.shapes() ?? [], breakLabels: () => candle.current?.priceLines().filter(l => l.options().title.startsWith('breaks')).map(l => ({ title: l.options().title, price: l.options().price, color: l.options().color })) ?? [], lastCandle: () => candle.current?.data().at(-1) ?? null, timeToCoordinate: (t: number) => c.timeScale().timeToCoordinate(t as UTCTimestamp) } });
    return () => { c.remove(); chart.current = null; candle.current = null; rail.current = null; band.current = null; bucket.current = null; strip.current = null; breakLines.current = null; patternLines.current = null; formingLines.current = null; indexSeries.current = null; bid.current = null; ask.current = null; };
  }, []);
  useEffect(() => {
    // Each coin keeps its own precision on the axis (DOGE 0.07530, BTC 77,300.50).
    const formatter = coinFormat(decimals);
    candle.current?.applyOptions({ priceFormat: { type: 'custom', formatter, minMove: 10 ** -decimals } });
    for (const s of [indexSeries.current]) s?.applyOptions({ priceFormat: { type: 'custom', formatter, minMove: 10 ** -decimals } });
    fitted.current = '';
  }, [decimals, coin]);
  useEffect(() => { band.current?.set(span); }, [span?.start, span?.end]);
  useEffect(() => { chart.current?.applyOptions({ timeScale: { secondsVisible: interval === '10s' || interval === '15s' || interval === '30s' } }); }, [interval]);
  // 15-minute windows carry their settled result; hourly periods use the candles (plan 033).
  const settled = desk.horizon === '15m' ? (desk.windows ?? []).filter(w => w.result === 'yes' || w.result === 'no') : [];
  const settledKey = settled.map(w => w.close_time + w.result).join(',');
  useEffect(() => { band.current?.setResults(new Map(settled.map(w => [Date.parse(w.close_time) / 1000, w.result === 'yes']))); }, [settledKey]);
  useEffect(() => {
    let dead = false; const ctrl = new AbortController();
    const load = async () => {
      try {
        const s = latest.current;
        const value = await get<ChartResponse>(`/api/v1/assets/${s.coin ?? 'BTC'}/candles?view_id=${s.view_id}&interval=${interval}&limit=300`, ctrl.signal);
        if (!dead) { setBars(value); setError(''); }
      } catch (e) { if (!dead) setError(e instanceof Error ? e.message : 'Chart unavailable'); }
    };
    void load();
    const timer = desk.mode === 'live' ? setInterval(load, 10000) : undefined;
    return () => { dead = true; ctrl.abort(); clearInterval(timer); };
  }, [interval, coin, desk.mode, desk.mode === 'replay' ? desk.view_id : null]);
  useEffect(() => {
    if (!bars || bars.interval !== interval || !candle.current) return;
    const { up, down, muted, gold } = colors.current;
    // Plan 041: completed reversal candles in gold, from these candles alone.
    // Plan 061: structure breaks, computed by the server on these same candles.
    const turns = new Map(reversalMarks ? (bars.reversals ?? []).map(m => [m.time, m]) : []);
    marked.current = [...turns.keys()];
    candle.current.setData(bars.candles.map(b => ({ time: b.time as UTCTimestamp, open: +b.open, high: +b.high, low: +b.low, close: +b.close,
      ...(!b.complete ? { color: muted, wickColor: muted } : turns.has(b.time) ? { color: gold, wickColor: gold, borderColor: gold } : {}) })));
    volume.current?.setData(bars.candles.map(b => ({ time: b.time as UTCTimestamp, value: +b.volume, color: alpha(+b.close >= +b.open ? up : down, .28) })));
    // A reload is a recorded view a moment old: a live candle the stream has already moved further is kept.
    const last = bars.candles.at(-1), was = live.current;
    const loaded = last ? { time: last.time, open: +last.open, high: +last.high, low: +last.low, close: +last.close } : null;
    live.current = desk.mode !== 'live' || !loaded || !was || was.time < loaded.time ? loaded
      : was.time === loaded.time ? { ...loaded, high: Math.max(loaded.high, was.high), low: Math.min(loaded.low, was.low), close: was.close } : was;
    if (live.current && loaded && live.current !== loaded) candle.current.update({ ...live.current, time: live.current.time as UTCTimestamp, color: muted, wickColor: muted });
    // Overlay warmup history can precede the returned candles. Keep every rendered series
    // on the candle timestamps so extra EMA history cannot shift the shared logical scale.
    const candleTimes = new Set(bars.candles.map(b => b.time));
    // Plan 065: the line never bridges a bar without a recorded tick or a gap in the candles, which would draw a level
    // the index never printed. A segment takes its starting point's colour, so the point before a break is clear.
    const ticks = new Map((bars.index ?? []).filter(b => candleTimes.has(b.time)).map(b => [b.time, b.value]));
    const step = INTERVAL_SECONDS[interval] ?? 60;
    indexSeries.current?.setData(indexLine ? bars.candles.flatMap((b, i) => {
      const v = ticks.get(b.time), next = bars.candles[i + 1];
      if (v == null) return [];
      const breaks = !next || next.time - b.time !== step || !ticks.has(next.time);
      return [{ time: b.time as UTCTimestamp, value: v, ...(breaks && next ? { color: 'rgba(0,0,0,0)' } : {}) }];
    }) : []);
    const seconds = INTERVAL_SECONDS[interval] ?? 60, newest = bars.candles.at(-1);
    const axis = newest ? { last: newest.time, seconds, count: bars.candles.length } : null;
    band.current?.setAxis(axis);
    strip.current?.setAxis(axis);
    const byTime = new Map(bars.candles.map(b => [b.time, b]));
    breakLines.current?.set(reversalMarks ? (bars.reversals ?? []).flatMap(m => {
      const c = byTime.get(m.time);
      return c ? [{ time: m.time, direction: m.direction, from: m.broke.time, price: Number(m.broke.price), tip: Number(m.direction === 'up' ? c.low : c.high) }] : [];
    }) : [], axis);
    // Plan 064: chart patterns, found by the server on these same candles.
    patternLines.current?.set(patternMarks ? bars.patterns ?? [] : [], axis);
    formingLines.current?.set(patternMarks ? bars.forming ?? [] : [], axis);
    band.current?.setBars(bars.candles.map(b => ({ time: b.time, open: +b.open, close: +b.close })));
    const key = interval + desk.mode + coin;
    if (fitted.current !== key && bars.candles.length) {
      chart.current?.timeScale().setVisibleLogicalRange({ from: Math.max(0, bars.candles.length - 90), to: bars.candles.length + 5 });
      fitted.current = key;
    }
  }, [bars, interval, desk.mode, reversalMarks, patternMarks, indexLine]);
  // Plan 057: the selected close's settlement window, and the index's newest tick on the newest bar between reloads.
  const close = desk.selected ? Date.parse(desk.selected.close_time) / 1000 : null;
  useEffect(() => { strip.current?.set(close); }, [close]);
  const tick = desk.benchmark;
  const settling = tick?.settlement ?? null;
  useEffect(() => {
    const series = indexSeries.current, bar = live.current;
    if (!indexLine || desk.mode !== 'live' || !series || !bar || !tick || tick.status !== 'current') return;
    if (!bars || bars.interval !== interval) return;
    const seconds = INTERVAL_SECONDS[interval] ?? 60, time = Math.floor(tick.at / seconds) * seconds;
    if (time === bar.time) series.update({ time: time as UTCTimestamp, value: Number(tick.value) });
  }, [tick?.at, indexLine, interval, desk.mode, bars]);
  const spot = desk.quotes.coinbase ? Number(desk.quotes.coinbase.price) : null;
  // Plan 047: every Coinbase price the stream brings (about every 100 ms) moves the newest candle, or opens the next
  // one when its time is in the next period, so the chart is as current as the Kalshi prices beside it. Display only.
  const quote = desk.quotes.coinbase;
  const quoteAt = quote ? (quote.source_at ? Date.parse(quote.source_at) / 1000 : quote.received_at) : null;
  useEffect(() => {
    const series = candle.current, bar = live.current;
    if (desk.mode !== 'live' || !series || !bar || spot == null || quoteAt == null || !Number.isFinite(spot)) return;
    if (!bars || bars.interval !== interval) return;
    const seconds = INTERVAL_SECONDS[interval] ?? 60, time = Math.floor(quoteAt / seconds) * seconds;
    if (time < bar.time) return;  // a quote from a period already drawn
    const next = time === bar.time ? { ...bar, high: Math.max(bar.high, spot), low: Math.min(bar.low, spot), close: spot }
      : { time, open: spot, high: spot, low: spot, close: spot };
    if (next.time === bar.time && next.close === bar.close && next.high === bar.high && next.low === bar.low) return;
    live.current = next;
    const { muted } = colors.current;
    series.update({ ...next, time: next.time as UTCTimestamp, color: muted, wickColor: muted });
  }, [spot, quoteAt, interval, desk.mode, bars]);
  // Only a 15-minute window's line is its target; an hourly strike is just the selected strike.
  const windowTarget = desk.horizon === '15m';
  const low = selectedBand?.low ?? null, high = selectedBand?.high ?? null;
  useEffect(() => {
    rail.current?.update(strikes, [selectedStrike, low, high].filter((v): v is number => v != null), spot);
  }, [strikes, selectedStrike, low, high, spot, bars]);
  useEffect(() => {
    const series = candle.current;
    bucket.current?.set(low != null && high != null ? { low, high } : null);
    if (!series || low == null || high == null) return;
    const edges = [low, high].map(price => series.createPriceLine({ price, color: token('--amber'), lineStyle: LineStyle.Solid, lineWidth: 1, axisLabelVisible: true, title: '' }));
    return () => { for (const l of edges) { try { series.removePriceLine(l); } catch { /* chart disposed */ } } };
  }, [low, high]);
  // A held selected strike shows the position's label on the axis instead of its own.
  const selectedHeld = levels.some(l => l.selected);
  useEffect(() => {
    const series = candle.current;
    if (selectedStrike == null || !series) return;
    const line: IPriceLine = series.createPriceLine({ price: selectedStrike, color: token('--amber'), lineStyle: LineStyle.Dashed, lineWidth: 2, axisLabelVisible: !selectedHeld, title: windowTarget && !selectedHeld ? 'target' : '' });
    return () => { try { series.removePriceLine(line); } catch { /* chart disposed */ } };
  }, [selectedStrike, windowTarget, selectedHeld]);
  // Each held position: its level in the side's colour, labelled on the axis with size, entry and P&L.
  const levelKey = levels.map(l => `${l.key}|${l.low}|${l.high}|${l.label}|${l.selected}`).join(',');
  useEffect(() => {
    const series = candle.current;
    if (!series || !levels.length) return;
    const lines = levels.flatMap(l => {
      const color = token(l.side === 'yes' ? '--up' : '--down');
      const top = l.high == null ? [] : [series.createPriceLine({ price: l.high, color, lineStyle: LineStyle.Solid, lineWidth: 2, axisLabelVisible: true, title: '' })];
      return [series.createPriceLine({ price: l.low, color, lineStyle: LineStyle.Solid, lineWidth: 2, axisLabelVisible: true, title: l.label }), ...top];
    });
    return () => { for (const l of lines) { try { series.removePriceLine(l); } catch { /* chart disposed */ } } };
  }, [levelKey]);
  const stopKey = coinStops.map(l => `${l.key}|${l.price}|${l.direction}`).join(',');
  useEffect(() => {
    const series = candle.current;
    if (!series || !coinStops.length) return;
    const lines = coinStops.map(l => series.createPriceLine({ price: l.price, color: token('--down'), lineStyle: LineStyle.Dashed, lineWidth: 2,
      axisLabelVisible: true, title: `stop: 15s close ${l.direction === 'up' ? 'below' : 'above'}` }));
    return () => { for (const l of lines) { try { series.removePriceLine(l); } catch { /* chart disposed */ } } };
  }, [stopKey]);
  // Plan 067: where each pattern forming now breaks, as axis labels only (the pattern draws its own lines): green to
  // break up, red to break down.
  const breaks = patternMarks && bars?.interval === interval ? (bars.forming ?? []).filter(p => p.status === 'forming').flatMap(p => [
    ...(p.break_up != null ? [{ price: Number(p.break_up), up: true }] : []), ...(p.break_down != null ? [{ price: Number(p.break_down), up: false }] : []),
  ]) : [];
  const breakKey = breaks.map(b => `${b.up}${b.price}`).join(',');
  useEffect(() => {
    const series = candle.current;
    if (!series || !breaks.length) return;
    const lines = breaks.map(b => series.createPriceLine({ price: b.price, color: token(b.up ? '--up' : '--down'), lineVisible: false,
      axisLabelVisible: true, title: b.up ? 'breaks up' : 'breaks down' }));
    return () => { for (const l of lines) { try { series.removePriceLine(l); } catch { /* chart disposed */ } } };
  }, [breakKey]);
  const seconds = INTERVAL_SECONDS[interval] ?? 60;
  // The contract pane: the held side's bid (what the position sells for) and ask, from recorded books.
  const held = contract?.ticker ?? null, heldSide = contract?.side ?? 'yes';
  useEffect(() => {
    const c = chart.current;
    if (!c || !held) return;
    const color = token(heldSide === 'yes' ? '--up' : '--down');
    const priceFormat = { type: 'custom' as const, formatter: centsFormat, minMove: 0.001 };
    bid.current = c.addSeries(LineSeries, { color, lineWidth: 2, priceFormat, lastValueVisible: true, priceLineVisible: false,
      // Keep the entry and both exits in view even when the price hasn't been near them.
      autoscaleInfoProvider: (base: () => AutoscaleInfo | null) => {
        const info = base(), all = [...exitLevels.current, ...(info?.priceRange ? [info.priceRange.minValue, info.priceRange.maxValue] : [])];
        return all.length ? { priceRange: { minValue: Math.min(...all), maxValue: Math.max(...all) } } : info;
      } }, 1);
    ask.current = c.addSeries(LineSeries, { color: alpha(token('--muted'), .6), lineWidth: 1, lineStyle: LineStyle.Dotted, priceFormat, lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false }, 1);
    // Room at the top for the legend and at the bottom for the floor's axis label.
    bid.current.priceScale().applyOptions({ scaleMargins: { top: .24, bottom: .1 } });
    // The candle pane starts at stretch 2, so 0.9 gives the contract pane about 30% of the height.
    c.panes()[1]?.setStretchFactor(0.9);
    return () => {
      for (const s of [bid.current, ask.current]) { if (s) { try { c.removeSeries(s); } catch { /* chart disposed */ } } }
      bid.current = null; ask.current = null;
      try { if (c.panes().length > 1) c.removePane(1); } catch { /* chart disposed */ }
    };
  }, [held, heldSide]);
  useEffect(() => {
    setPrices(null);
    if (!held) return;
    let dead = false; const ctrl = new AbortController();
    const load = async () => {
      try {
        const value = await get<ContractPrices>(`/api/v1/contracts/${encodeURIComponent(held)}/prices?view_id=${latest.current.view_id}&interval=${interval}`, ctrl.signal);
        if (!dead) setPrices(value);
      } catch { /* the pane stays empty until the next load */ }
    };
    void load();
    const timer = desk.mode === 'live' ? setInterval(load, 10000) : undefined;
    return () => { dead = true; ctrl.abort(); clearInterval(timer); };
  }, [held, interval, desk.mode, desk.mode === 'replay' ? desk.view_id : null]);
  useEffect(() => {
    if (!bid.current || !ask.current) return;
    if (!prices || prices.ticker !== held || prices.interval !== interval || !bars) { bid.current.setData([]); ask.current.setData([]); return; }
    // Keep the pane on the candle timestamps, like the EMAs, so it cannot shift the shared time scale.
    const times = new Set(bars.candles.map(b => b.time));
    const rows = prices.bars.filter(b => times.has(b.time));
    const pick = (b: typeof rows[number], kind: 'bid' | 'ask') => b[`${heldSide}_${kind}`];
    bid.current.setData(rows.flatMap(b => pick(b, 'bid') == null ? [] : [{ time: b.time as UTCTimestamp, value: Number(pick(b, 'bid')) }]));
    ask.current.setData(rows.flatMap(b => pick(b, 'ask') == null ? [] : [{ time: b.time as UTCTimestamp, value: Number(pick(b, 'ask')) }]));
  }, [prices, bars, held, heldSide, interval]);
  // The streamed book moves the current bar between loads.
  const book = desk.book && desk.book.ticker === held ? desk.book : null;
  useEffect(() => {
    const series = bid.current, newest = bars?.candles.at(-1);
    if (!book || !series || !newest) return;
    const t = Math.floor(book.observed_at / seconds) * seconds, last = series.data().at(-1);
    if (t !== newest.time || (last && Number(last.time) > t)) return;
    const own = bestBid(heldSide === 'yes' ? book.yes : book.no), other = bestBid(heldSide === 'yes' ? book.no : book.yes);
    if (own != null) series.update({ time: t as UTCTimestamp, value: own });
    if (other != null && ask.current && !(Number(ask.current.data().at(-1)?.time ?? 0) > t)) ask.current.update({ time: t as UTCTimestamp, value: 1 - other });
  }, [book?.revision, held, bars]);
  const exitKey = contract ? `${contract.entry}|${contract.takeProfit}` : '';
  useEffect(() => {
    exitLevels.current = contract ? [contract.entry, contract.takeProfit].filter((v): v is number => v != null) : [];
    const series = bid.current;
    if (!series || !contract) return;
    const lines = [
      { price: contract.entry, color: token('--text'), style: LineStyle.Dashed },
      contract.takeProfit != null && { price: contract.takeProfit, color: token('--up'), style: LineStyle.Solid },
    ].flatMap(l => l ? [series.createPriceLine({ price: l.price, color: l.color, lineStyle: l.style, lineWidth: 1, axisLabelVisible: true, title: '' })] : []);
    return () => { for (const l of lines) { try { series.removePriceLine(l); } catch { /* chart disposed */ } } };
  }, [exitKey, held, heldSide]);
  return <div className="chart-wrap">
    <div ref={host} className={`price-chart${contract ? ' with-contract' : ''}`} data-testid="price-chart" role="img" aria-label={`Coinbase ${coin}/USD ${interval} candlestick chart with the ${windowTarget ? 'window target' : selectedBand ? 'selected range' : 'selected strike'}`}/>
    {contract && paneTop > 0 && <div className="contract-legend" data-testid="contract-pane" style={{ top: paneTop + 6 }}>
      <span><i className="sw" style={{ background: `var(${contract.side === 'yes' ? '--up' : '--down'})` }}/>{contract.name} bid</span>
      <span><i className="sw dashed"/>Entry {centsFormat(contract.entry)}</span>
      {contract.takeProfit != null && <span className="tp">TP {centsFormat(contract.takeProfit)}</span>}
    </div>}
    {settling && <div className="settle-avg" data-testid="settle-avg" title={`Kalshi settles on the average of ${tick!.index_id} over the 60 seconds before the close`}>
      Settlement avg <b className="num">{settling.average == null ? '—' : coinFormat(decimals)(Number(settling.average))}</b> · {settling.count}/{settling.needed}</div>}
    {!bars?.candles.length && <div className="chart-empty">{error || ((bars?.interval === '10s' || bars?.interval === '15s' || bars?.interval === '30s') && bars.interval === interval
      ? `No ${bars.interval.replace('s', '')}-second candles in this recorded view yet. They are built from recorded trades as each one closes.`
      : 'Loading recorded candles…')}</div>}
    <div className="chart-meta">{error || (bars ? `${bars.candles.length} bars · ${bars.gaps ? `${bars.gaps} gaps` : 'continuous'}` : '')}</div>
  </div>;
}
